import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Button,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
  FormFeedback,
  Table,
  Badge,
} from "reactstrap";
import { Eye, Check, Plus, X, ChevronLeft, ChevronRight } from "lucide-react";
import Breadcrumbs from "../../../Components/Common/Breadcrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import ProductCard from "../../../Components/ProductCard";

const baseUrl = process.env.REACT_APP_BASE_URL;

interface PartnerSubmission {
  id: number;
  partner_name: string;
  partner_email: string;
  domain_id: number;
  domain_name: string;
  status: string;
  expires_at: string;
  is_expired: boolean;
  product_count: number;
  created_at: string;
  submitted_at: string | null;
  approved_at: string | null;
  created_by_name: string | null;
  approved_by_name: string | null;
}

interface Domain {
  id: number;
  name: string;
}

interface Partner {
  id: string;
  user_id: string;
  name: string;
  email: string;
}

// Helper function to convert text to title case
const toTitleCase = (str: string): string => {
  if (!str) return "";
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const PartnerPlanSubmissions = () => {
  document.title = "Partner Plan Submissions | BizServe";

  const [submissions, setSubmissions] = useState<PartnerSubmission[]>([]);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ show: boolean; type: string; message: string }>({
    show: false,
    type: "success",
    message: "",
  });

  // Create link modal
  const [createModal, setCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    partner_name: "",
    partner_email: "",
    domain_id: "",
    expiry_days: "30",
  });
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});
  const [creating, setCreating] = useState(false);

  // View submission modal
  const [viewModal, setViewModal] = useState(false);
  const [viewingSubmission, setViewingSubmission] = useState<any>(null);
  const [viewingProducts, setViewingProducts] = useState<any[]>([]);
  const [carouselIndices, setCarouselIndices] = useState<Record<string, number>>({});

  // Approve confirmation modal
  const [approveModal, setApproveModal] = useState(false);
  const [approvingSubmissionId, setApprovingSubmissionId] = useState<number | null>(null);
  const [approvingSubmissionDomainId, setApprovingSubmissionDomainId] = useState<number | null>(null);
  const [approving, setApproving] = useState(false);
  const [domainPartners, setDomainPartners] = useState<Partner[]>([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState<string>("");
  const [partnerError, setPartnerError] = useState<string>("");

  useEffect(() => {
    fetchSubmissions();
    fetchDomains();
  }, []);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await axios.get("/payments/listPartnerSubmissions/", {
        withCredentials: true,
      });
      if (response && response.status === "SENT") {
        setSubmissions(response.submissions || []);
      }
    } catch (error: any) {
      console.error("Error fetching submissions:", error);
      showAlert("danger", "Failed to load submissions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchDomains = async () => {
    try {
      const response = await axios.get("/accounts/getdomains", {
        withCredentials: true,
      });
      if (response && response.status === "SENT") {
        setDomains(response.domains || []);
      }
    } catch (error: any) {
      console.error("Error fetching domains:", error);
    }
  };

  const showAlert = (type: string, message: string) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: "success", message: "" }), 5000);
  };

  const handleCreateLink = async () => {
    // Validate
    const errors: Record<string, string> = {};
    if (!createForm.partner_name.trim()) {
      errors.partner_name = "Partner name is required";
    }
    if (!createForm.partner_email.trim()) {
      errors.partner_email = "Partner email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createForm.partner_email)) {
      errors.partner_email = "Invalid email address";
    }
    if (!createForm.domain_id) {
      errors.domain_id = "Domain is required";
    }
    const expiryDays = parseInt(createForm.expiry_days);
    if (isNaN(expiryDays) || expiryDays < 1) {
      errors.expiry_days = "Expiry days must be at least 1";
    }

    if (Object.keys(errors).length > 0) {
      setCreateErrors(errors);
      return;
    }

    try {
      setCreating(true);
      setCreateErrors({});
      const response = await axios.post(
        "/payments/createPartnerSubmissionLink/",
        {
          partner_name: createForm.partner_name.trim(),
          partner_email: createForm.partner_email.trim(),
          domain_id: parseInt(createForm.domain_id),
          expiry_days: expiryDays,
        },
        { withCredentials: true }
      );

      if (response && response.status === "SENT") {
        showAlert("success", `Submission link created and sent to ${createForm.partner_email}`);
        setCreateModal(false);
        setCreateForm({
          partner_name: "",
          partner_email: "",
          domain_id: "",
          expiry_days: "30",
        });
        fetchSubmissions();
      } else {
        showAlert("danger", response.error || "Failed to create submission link");
      }
    } catch (error: any) {
      console.error("Error creating submission link:", error);
      const errorMsg = error.response?.data?.error || "Failed to create submission link. Please try again.";
      showAlert("danger", errorMsg);
    } finally {
      setCreating(false);
    }
  };

  const fetchDomainPartners = async (domainId: number) => {
    try {
      setLoadingPartners(true);
      setPartnerError("");
      const response = await axios.get("/payments/listDomainPartners", {
        params: { domain: domainId },
        withCredentials: true,
      });
      if (response && response.status === "SENT") {
        setDomainPartners(response.partners || []);
      } else {
        setDomainPartners([]);
        setPartnerError("Failed to load partners");
      }
    } catch (error: any) {
      console.error("Error fetching domain partners:", error);
      setDomainPartners([]);
      setPartnerError("Failed to load partners. Please try again.");
    } finally {
      setLoadingPartners(false);
    }
  };

  const handleViewSubmission = async (submissionId: number) => {
    try {
      const response = await axios.get(`/payments/viewPartnerSubmission/${submissionId}/`, {
        withCredentials: true,
      });
      if (response && response.status === "SENT") {
        setViewingSubmission(response.submission);
        setViewingProducts(response.products || []);
        // Initialize carousel indices for each product type
        const indices: Record<string, number> = {};
        (response.products || []).forEach((p: any) => {
          if (indices[p.product_type] === undefined) {
            indices[p.product_type] = 0;
          }
        });
        setCarouselIndices(indices);
        setViewModal(true);
      } else {
        showAlert("danger", response.error || "Failed to load submission");
      }
    } catch (error: any) {
      console.error("Error viewing submission:", error);
      showAlert("danger", "Failed to load submission. Please try again.");
    }
  };

  const handleApproveSubmission = async () => {
    if (!approvingSubmissionId) return;

    // Validate partner selection
    if (!selectedPartnerId) {
      setPartnerError("Please select a partner");
      return;
    }

    try {
      setApproving(true);
      setPartnerError("");
      const response = await axios.post(
        "/payments/approvePartnerSubmission/",
        { 
          submission_id: approvingSubmissionId,
          partner_id: selectedPartnerId
        },
        { withCredentials: true }
      );

      if (response && response.status === "APPROVED") {
        showAlert("success", `Submission approved. ${response.created_products?.length || 0} product(s) created.`);
        setApproveModal(false);
        setApprovingSubmissionId(null);
        setApprovingSubmissionDomainId(null);
        setSelectedPartnerId("");
        setDomainPartners([]);
        fetchSubmissions();
      } else {
        showAlert("danger", response.error || "Failed to approve submission");
      }
    } catch (error: any) {
      console.error("Error approving submission:", error);
      const errorMsg = error.response?.data?.error || "Failed to approve submission. Please try again.";
      showAlert("danger", errorMsg);
    } finally {
      setApproving(false);
    }
  };

  const getStatusBadge = (status: string, isExpired: boolean) => {
    if (isExpired && status === "draft") {
      return <Badge color="secondary">Expired</Badge>;
    }
    switch (status) {
      case "draft":
        return <Badge color="info">Draft</Badge>;
      case "submitted":
        return <Badge color="warning">Submitted</Badge>;
      case "approved":
        return <Badge color="success">Approved</Badge>;
      case "rejected":
        return <Badge color="danger">Rejected</Badge>;
      default:
        return <Badge color="secondary">{status}</Badge>;
    }
  };

  const columns = [
    {
      header: "Partner",
      accessorKey: "partner_name",
      enableColumnFilter: true,
      enableSorting: true,
      cell: (info: any) => {
        const row = info.row.original;
        return (
          <div>
            <div style={{ fontWeight: 600 }}>{row.partner_name}</div>
            <div style={{ fontSize: "0.875rem", color: "#666" }}>{row.partner_email}</div>
          </div>
        );
      },
    },
    {
      header: "Domain",
      accessorKey: "domain_name",
      enableColumnFilter: true,
      enableSorting: true,
      cell: (info: any) => {
        return toTitleCase(info.getValue() || "");
      },
    },
    {
      header: "Status",
      accessorKey: "status",
      enableColumnFilter: true,
      enableSorting: true,
      cell: (info: any) => {
        const row = info.row.original;
        return getStatusBadge(row.status, row.is_expired);
      },
    },
    {
      header: "Products",
      accessorKey: "product_count",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (info: any) => {
        const count = info.getValue();
        return <span style={{ fontWeight: 600 }}>{count}</span>;
      },
    },
    {
      header: "Created",
      accessorKey: "created_at",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (info: any) => {
        const date = new Date(info.getValue());
        return date.toLocaleDateString();
      },
    },
    {
      header: "Submitted",
      accessorKey: "submitted_at",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (info: any) => {
        const date = info.getValue();
        return date ? new Date(date).toLocaleDateString() : "-";
      },
    },
    {
      header: "Actions",
      accessorKey: "id",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (info: any) => {
        const row = info.row.original;
        return (
          <div className="d-flex gap-2">
            <Button
              color="info"
              size="sm"
              onClick={() => handleViewSubmission(row.id)}
              title="View Submission"
            >
              <Eye size={16} />
            </Button>
            {row.status === "submitted" && (
              <Button
                color="success"
                size="sm"
                onClick={() => {
                  setApprovingSubmissionId(row.id);
                  setApprovingSubmissionDomainId(row.domain_id);
                  setSelectedPartnerId("");
                  setDomainPartners([]);
                  setPartnerError("");
                  fetchDomainPartners(row.domain_id);
                  setApproveModal(true);
                }}
                title="Approve Submission"
              >
                <Check size={16} />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Partner Plan Submissions" breadcrumbItem="Submissions" />
          
          {alert.show && (
            <Alert color={alert.type} className="mt-3">
              {alert.message}
            </Alert>
          )}

          <Row>
            <Col md={12}>
              <div className="d-flex justify-content-end align-items-start gap-2 mb-3">
                <Button color="primary" onClick={() => setCreateModal(true)} className="d-inline-flex align-items-center">
                  <Plus size={16} className="me-1" />
                  Create Submission Link
                </Button>
              </div>
            </Col>
            <Col md={12}>
              <Card className="rounded-4 border">
                <CardHeader>
                  <h4 className="card-title mb-0">Partner Plan Submissions</h4>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={submissions}
                      isGlobalFilter={true}
                      isPagination={true}
                      SearchPlaceholder="Search by partner name or email..."
                      pagination="pagination"
                      paginationWrapper="dataTables_paginate paging_simple_numbers"
                      tableClass="table-bordered dt-responsive table-nowrap w-100 dataTable no-footer dtr-inline"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Create Link Modal */}
          <Modal isOpen={createModal} toggle={() => setCreateModal(false)} centered className="rounded-4">
            <ModalHeader toggle={() => setCreateModal(false)}>
              <div className="modal-title mt-0 font-size-16 text-dark">Create Submission Link</div>
            </ModalHeader>
            <ModalBody>
              <div className="mb-3">
                <Label>Partner Name <span className="text-danger">*</span></Label>
                <Input
                  type="text"
                  value={createForm.partner_name}
                  onChange={(e) => setCreateForm({ ...createForm, partner_name: e.target.value })}
                  invalid={!!createErrors.partner_name}
                />
                {createErrors.partner_name && <FormFeedback>{createErrors.partner_name}</FormFeedback>}
              </div>
              <div className="mb-3">
                <Label>Partner Email <span className="text-danger">*</span></Label>
                <Input
                  type="email"
                  value={createForm.partner_email}
                  onChange={(e) => setCreateForm({ ...createForm, partner_email: e.target.value })}
                  invalid={!!createErrors.partner_email}
                />
                {createErrors.partner_email && <FormFeedback>{createErrors.partner_email}</FormFeedback>}
              </div>
              <div className="mb-3">
                <Label>Domain <span className="text-danger">*</span></Label>
                <Input
                  type="select"
                  value={createForm.domain_id}
                  onChange={(e) => setCreateForm({ ...createForm, domain_id: e.target.value })}
                  invalid={!!createErrors.domain_id}
                >
                  <option value="">Select Domain</option>
                  {domains.map((domain) => (
                    <option key={domain.id} value={domain.id}>
                      {toTitleCase(domain.name)}
                    </option>
                  ))}
                </Input>
                {createErrors.domain_id && <FormFeedback>{createErrors.domain_id}</FormFeedback>}
              </div>
              <div className="mb-3">
                <Label>Expiry Days <span className="text-danger">*</span></Label>
                <Input
                  type="number"
                  min="1"
                  value={createForm.expiry_days}
                  onChange={(e) => setCreateForm({ ...createForm, expiry_days: e.target.value })}
                  invalid={!!createErrors.expiry_days}
                />
                <small className="text-muted">Number of days until the link expires</small>
                {createErrors.expiry_days && <FormFeedback>{createErrors.expiry_days}</FormFeedback>}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setCreateModal(false)} disabled={creating}>
                Cancel
              </Button>
              <Button color="primary" onClick={handleCreateLink} disabled={creating}>
                {creating ? "Creating..." : "Create & Send Link"}
              </Button>
            </ModalFooter>
          </Modal>

          {/* View Submission Modal */}
          <Modal
            isOpen={viewModal}
            toggle={() => setViewModal(false)}
            centered
            className="rounded-4"
            size="xl"
          >
            <ModalHeader toggle={() => setViewModal(false)}>
              <div className="modal-title mt-0 font-size-16 text-dark">View Submission</div>
            </ModalHeader>
            <ModalBody>
              {viewingSubmission && (
                <div>
                  <div className="mb-3">
                    <strong>Partner:</strong> {viewingSubmission.partner_name} ({viewingSubmission.partner_email})
                  </div>
                  <div className="mb-3">
                    <strong>Domain:</strong> {toTitleCase(viewingSubmission.domain_name)}
                  </div>
                  <div className="mb-3">
                    <strong>Status:</strong> {getStatusBadge(viewingSubmission.status, viewingSubmission.is_expired)}
                  </div>
                  <div className="mb-3">
                    <strong>Products:</strong> {viewingProducts.length}
                  </div>
                  <hr />
                  <h5>Products ({viewingProducts.length})</h5>
                  {viewingProducts.length > 0 ? (
                    <div>
                      {["plan", "subscriptionaddon", "planoptionals", "addon", "onetimeproduct"].map((type) => {
                        const typeProducts = viewingProducts.filter((p: any) => p.product_type === type);
                        if (typeProducts.length === 0) return null;

                        const typeLabel = type === "plan" ? "Plan" :
                          type === "subscriptionaddon" ? "Subscription Add-On" :
                          type === "planoptionals" ? "Plan Optionals" :
                          type === "addon" ? "Add-On" : "One-Time Product";

                        return (
                          <div key={type} className="mb-4">
                            <h6 className="mb-3">
                              {typeLabel} ({typeProducts.length})
                            </h6>
                            <Row>
                              {typeProducts.map((product: any) => (
                                <ProductCard
                                  key={product.id}
                                  product={product}
                                  typeLabel={typeLabel}
                                  showActions={false}
                                />
                              ))}
                            </Row>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-muted">No products in this submission.</p>
                  )}
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => setViewModal(false)}>
                Close
              </Button>
            </ModalFooter>
          </Modal>

          {/* Approve Confirmation Modal */}
          <Modal
            isOpen={approveModal}
            toggle={() => {
              setApproveModal(false);
              setSelectedPartnerId("");
              setDomainPartners([]);
              setPartnerError("");
            }}
            centered
            className="rounded-4"
          >
            <ModalHeader toggle={() => {
              setApproveModal(false);
              setSelectedPartnerId("");
              setDomainPartners([]);
              setPartnerError("");
            }}>
              <div className="modal-title mt-0 font-size-16 text-dark">Approve Submission</div>
            </ModalHeader>
            <ModalBody>
              <p className="mb-3">Are you sure you want to approve this submission? This will create products in the main system.</p>
              
              <div className="mb-3">
                <Label>Select Partner <span className="text-danger">*</span></Label>
                {loadingPartners ? (
                  <div className="text-center py-2">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <small className="text-muted ms-2">Loading partners...</small>
                  </div>
                ) : (
                  <Input
                    type="select"
                    value={selectedPartnerId}
                    onChange={(e) => {
                      setSelectedPartnerId(e.target.value);
                      setPartnerError("");
                    }}
                    invalid={!!partnerError}
                  >
                    <option value="">Select Partner</option>
                    {domainPartners.map((partner) => (
                      <option key={partner.id} value={partner.id}>
                        {toTitleCase(partner.name || partner.email || `Partner ${partner.id}`)}
                      </option>
                    ))}
                  </Input>
                )}
                {partnerError && <FormFeedback>{partnerError}</FormFeedback>}
                {!loadingPartners && domainPartners.length === 0 && !partnerError && (
                  <small className="text-muted">No partners found for this domain.</small>
                )}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button 
                color="secondary" 
                onClick={() => {
                  setApproveModal(false);
                  setSelectedPartnerId("");
                  setDomainPartners([]);
                  setPartnerError("");
                }} 
                disabled={approving}
              >
                Cancel
              </Button>
              <Button 
                color="success" 
                onClick={handleApproveSubmission} 
                disabled={approving || !selectedPartnerId || loadingPartners}
              >
                {approving ? "Approving..." : "Approve"}
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default PartnerPlanSubmissions;
